import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePostContext } from '../context/PostContext';
import { useUserContext } from '../context/UserContext';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import EmojiPicker from 'emoji-picker-react';
import UserAvatar from './UserAvatar';
import { FaBuilding } from 'react-icons/fa';

const MEDIA_BASE = 'https://buzzinguniverse.com/backend';
const GIPHY_KEY = '8FL5QcGl28D9gd1GvoG246swmDsiZLja';

const CommentBox = ({ post, setShowCommentBox, comment_id, GetPoost }) => {
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));
    const { addCommentToPost } = usePostContext();
    const { myFreinds, Notify, activeProfile, myAdminEntities } = useUserContext();

    // Identity for this comment only — defaults to the globally active profile,
    // but can be switched locally without changing the overall profile.
    const [commentAs, setCommentAs] = useState(activeProfile);
    const [showSwitcher, setShowSwitcher] = useState(false);
    const switcherRef = useRef(null);
    useEffect(() => { setCommentAs(activeProfile); }, [activeProfile?.id]);

    const personalPhoto = auth?.photo ? (auth.photo.startsWith('http') ? auth.photo : `${MEDIA_BASE}/${auth.photo}`) : '/images/default.png';
    const actorName  = commentAs ? commentAs.name : `${auth?.fname || ''} ${auth?.lname || ''}`.trim();
    const actorAvatar = commentAs ? (commentAs.photo || null) : personalPhoto;
    const actorLink  = commentAs
        ? `/company/${commentAs.id}/${encodeURIComponent(commentAs.name)}`
        : `/member/${auth?.id}/profile`;
    const canSwitch = (myAdminEntities || []).length > 0;

    const getEntityPhoto = (entity) => {
        if (entity.profile_picture) return `${MEDIA_BASE}/${entity.profile_picture}`;
        return entity.type === 'company' ? null : '/images/mystery-group.png';
    };

    const selectCommentAs = (entity) => {
        setCommentAs(entity ? {
            type: entity.type || 'group',
            id: entity.id,
            name: entity.name,
            photo: getEntityPhoto(entity),
        } : null);
        setShowSwitcher(false);
    };

    const [comment, setComment] = useState('');
    const [tagIds, setTagIds] = useState([]);
    const [mentionResults, setMentionResults] = useState({ users: [], groups: [], companies: [] });
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [gifSearch, setGifSearch] = useState('');
    const [gifs, setGifs] = useState([]);
    const [gifLoading, setGifLoading] = useState(false);
    const [attachment, setAttachment] = useState(null); // { type: 'image'|'gif', file?, previewUrl?, url? }

    const inputRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const gifPickerRef = useRef(null);
    const gifButtonRef = useRef(null);
    const imageInputRef = useRef(null);
    const typingDebounce = useRef(null);
    const mentionDebounce = useRef(null);

    useEffect(() => {
        const mentionIds = [...(inputRef.current?.querySelectorAll('.mention') || [])].map(
            (el) => parseInt(el.getAttribute('nav_id'))
        );
        setTagIds(mentionIds);
    }, [comment]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(e.target) &&
                !emojiButtonRef.current?.contains(e.target)
            ) setShowEmojiPicker(false);

            if (
                gifPickerRef.current &&
                !gifPickerRef.current.contains(e.target) &&
                !gifButtonRef.current?.contains(e.target)
            ) setShowGifPicker(false);

            if (switcherRef.current && !switcherRef.current.contains(e.target)) setShowSwitcher(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showGifPicker && gifs.length === 0) fetchGifs('trending');
    }, [showGifPicker]);

    const fetchGifs = async (query) => {
        setGifLoading(true);
        try {
            const url = query === 'trending'
                ? `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=18&rating=g`
                : `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=18&rating=g`;
            const res = await fetch(url);
            const data = await res.json();
            setGifs(data.data || []);
        } catch (err) {
            console.error('GIF fetch error', err);
        } finally {
            setGifLoading(false);
        }
    };

    const handleGifSearchChange = (e) => {
        const q = e.target.value;
        setGifSearch(q);
        if (q.trim().length > 1) fetchGifs(q.trim());
        else if (q.trim().length === 0) fetchGifs('trending');
    };

    const selectGif = (gif) => {
        setAttachment({ type: 'gif', url: gif.images.fixed_height.url });
        setShowGifPicker(false);
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAttachment({ type: 'image', file, previewUrl: URL.createObjectURL(file) });
    };

    const removeAttachment = () => {
        if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
        setAttachment(null);
        if (imageInputRef.current) imageInputRef.current.value = '';
    };

    const insertEmoji = (emojiData) => {
        const el = inputRef.current;
        const newHTML = (el.innerHTML || '') + emojiData.emoji;
        el.innerHTML = newHTML;
        setComment(newHTML);
        el.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        setShowEmojiPicker(false);
    };

    const broadcastTyping = () => {
        if (!auth?.id || !post?.post_data?.id || typingDebounce.current) return;
        axios.post('https://buzzinguniverse.com/backend/api/comment-typing', {
            post_id: post.post_data.id,
            user_id: auth.id,
            user_name: actorName,
        }).catch(() => {});
        typingDebounce.current = setTimeout(() => { typingDebounce.current = null; }, 2000);
    };

    const friendsAsMentionResults = () => ({
        users: (myFreinds || []).map((f) => f.user_data),
        groups: [],
        companies: [],
    });

    const searchMentions = (query) => {
        if (mentionDebounce.current) clearTimeout(mentionDebounce.current);
        mentionDebounce.current = setTimeout(() => {
            axios.get(`https://buzzinguniverse.com/backend/api/search?keyword=${encodeURIComponent(query)}`)
                .then((res) => {
                    const data = res.data?.data || {};
                    setMentionResults({
                        users: data.users || [],
                        groups: data.groups || [],
                        companies: data.companies || [],
                    });
                })
                .catch(() => setMentionResults({ users: [], groups: [], companies: [] }));
        }, 300);
    };

    const handleInputChange = (e) => {
        const newHtml = e.target.innerHTML;
        const newText = e.target.textContent;
        setComment(newHtml);
        broadcastTyping();

        const atIndex = newText.lastIndexOf('@');
        if (atIndex !== -1) {
            const searchQuery = newText.substring(atIndex + 1);
            if (searchQuery.length > 40 || searchQuery.includes('\n')) {
                setShowDropdown(false);
            } else if (searchQuery.length === 0) {
                // No query yet — show friends as a quick default
                setMentionResults(friendsAsMentionResults());
                setShowDropdown(true);
            } else {
                searchMentions(searchQuery);
                setShowDropdown(true);
            }
        } else {
            setShowDropdown(false);
        }
    };

    const handleMentionClick = (item, type) => {
        const textBeforeMention = comment.substring(0, comment.lastIndexOf('@'));
        const displayName = type === 'user' ? `${item.first_name} ${item.last_name}` : item.name;
        const mentionHTML = `<span class="mention" nav_id="${item.id}" mention_type="${type}" nav_name="${encodeURIComponent(displayName)}">@${displayName}</span>&nbsp;`;
        const updatedComment = `${textBeforeMention}${mentionHTML}`;
        inputRef.current.innerHTML = updatedComment;
        setComment(updatedComment);
        setShowDropdown(false);
        setTimeout(() => {
            const el = inputRef.current;
            el.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }, 0);
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        const hasText = comment.trim() && comment.trim() !== '<br>';
        if (!hasText && !attachment) return;
        setLoading(true);
        try {
            let finalMessage = comment;

            if (attachment) {
                if (attachment.type === 'gif') {
                    finalMessage += `<br><img src="${attachment.url}" class="comment-media-img" alt="gif">`;
                } else if (attachment.type === 'image') {
                    const uploadData = new FormData();
                    uploadData.append('file', attachment.file);
                    const uploadRes = await axios.post('https://buzzinguniverse.com/backend/api/upload-temp', uploadData);
                    if (uploadRes.data.code === 200) {
                        finalMessage += `<br><img src="${uploadRes.data.url}" class="comment-media-img" alt="image">`;
                    }
                }
            }

            const commentPayload = {
                post_id: post.post_data.id,
                user_id: auth?.id,
                message: finalMessage,
                comment_id: comment_id || null,
                tag_ids: tagIds.join(','),
            };
            if (commentAs) commentPayload.group_id = commentAs.id;
            const response = await axios.post('https://buzzinguniverse.com/backend/api/comment-request', commentPayload);

            if (response.status === 200) {
                setComment('');
                Notify(post.post_data.user_id, `${actorName} commented on your post`, `${actorLink}/post/${post.post_data.id}`);
                inputRef.current.innerHTML = '';
                setTagIds([]);
                removeAttachment();
                addCommentToPost(post.post_data.id, response.data.data);
                GetPoost && GetPoost();
                setShowCommentBox(false);
            }
        } catch (error) {
            console.error('Error posting comment:', error);
        } finally {
            setLoading(false);
        }
    };

    const hasContent = (comment.trim() && comment.trim() !== '<br>') || !!attachment;

    return (
        <div className="cb-wrapper">
            {canSwitch ? (
                <div className="cb-switcher-wrap" ref={switcherRef}>
                    <button
                        type="button"
                        className="cb-switcher-btn"
                        title={`Commenting as ${actorName} — click to switch`}
                        onClick={() => setShowSwitcher(p => !p)}
                    >
                        <UserAvatar
                            src={actorAvatar}
                            alt="img"
                            className="cb-avatar"
                            isCompany={!!commentAs}
                        />
                        <span className="cb-switcher-caret">▾</span>
                    </button>

                    {showSwitcher && (
                        <div className="cb-switcher-popup">
                            <div className="nav-switch-label">Comment as</div>
                            <div
                                className={`nav-switch-item ${!commentAs ? 'nav-switch-active' : ''}`}
                                onClick={() => selectCommentAs(null)}
                            >
                                <UserAvatar src={personalPhoto} alt="personal" className="nav-switch-avatar" />
                                <span>{auth?.fname} {auth?.lname} <small>(Personal)</small></span>
                                {!commentAs && <span className="nav-switch-tick">✓</span>}
                            </div>
                            {myAdminEntities.map((entity) => {
                                const photo = getEntityPhoto(entity);
                                return (
                                    <div
                                        key={entity.id}
                                        className={`nav-switch-item ${commentAs?.id === entity.id ? 'nav-switch-active' : ''}`}
                                        onClick={() => selectCommentAs(entity)}
                                    >
                                        {photo ? (
                                            <img src={photo} alt={entity.name} className="nav-switch-avatar" />
                                        ) : (
                                            <div className="nav-switch-avatar nav-company-placeholder"><FaBuilding /></div>
                                        )}
                                        <span>{entity.name} <small>({entity.type === 'company' ? 'Company' : 'Group'})</small></span>
                                        {commentAs?.id === entity.id && <span className="nav-switch-tick">✓</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                <Link to={actorLink}>
                    <UserAvatar
                        src={actorAvatar}
                        alt="img"
                        className="cb-avatar"
                        isCompany={!!commentAs}
                    />
                </Link>
            )}

            <div className="cb-body">
                <form onSubmit={handleCommentSubmit}>
                    <div className="cb-pill">
                        {/* Text input */}
                        <div
                            ref={inputRef}
                            className="cb-input"
                            contentEditable
                            onInput={handleInputChange}
                            data-placeholder="Add a comment..."
                            style={{ fontFamily: '"Noto Color Emoji", -apple-system, BlinkMacSystemFont, "Segoe UI", "Segoe UI Emoji", "Segoe UI Symbol", "Apple Color Emoji", Roboto, Helvetica, Arial, sans-serif' }}
                        />

                        {/* Action icons */}
                        <div className="cb-actions">
                            {/* Emoji */}
                            <button
                                type="button"
                                ref={emojiButtonRef}
                                className="cb-icon-btn"
                                title="Emoji"
                                onClick={() => { setShowEmojiPicker(p => !p); setShowGifPicker(false); }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
                                    <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    <circle cx="9" cy="10" r="1" fill="currentColor"/>
                                    <circle cx="15" cy="10" r="1" fill="currentColor"/>
                                </svg>
                            </button>

                            {/* GIF */}
                            <button
                                type="button"
                                ref={gifButtonRef}
                                className="cb-icon-btn cb-gif-label"
                                title="GIF"
                                onClick={() => { setShowGifPicker(p => !p); setShowEmojiPicker(false); }}
                            >
                                GIF
                            </button>

                            {/* Image */}
                            <button
                                type="button"
                                className="cb-icon-btn"
                                title="Photo"
                                onClick={() => imageInputRef.current?.click()}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                                    <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <input
                                type="file"
                                ref={imageInputRef}
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageSelect}
                            />

                            {/* Send */}
                            {hasContent && (
                                <button type="submit" className="cb-send-btn" disabled={loading} title="Post">
                                    {loading ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.5 }}>
                                            <circle cx="12" cy="12" r="10"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                        </svg>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Mention dropdown */}
                    {showDropdown && (mentionResults.users.length > 0 || mentionResults.groups.length > 0 || mentionResults.companies.length > 0) && (
                        <ul className="friends-dropdown">
                            {mentionResults.users.map((user) => {
                                const photo = user.photo
                                    ? (user.photo.startsWith('http') ? user.photo : `https://buzzinguniverse.com/backend/${user.photo}`)
                                    : '/images/default.png';
                                return (
                                    <li key={`user-${user.id}`} onClick={() => handleMentionClick(user, 'user')}>
                                        <img src={photo} alt="" className="mention-avatar" onError={e => { e.target.src = '/images/default.png'; }} />
                                        <span>{user.first_name} {user.last_name}</span>
                                    </li>
                                );
                            })}
                            {mentionResults.groups.map((group) => {
                                const photo = group.profile_picture
                                    ? (group.profile_picture.startsWith('http') ? group.profile_picture : `https://buzzinguniverse.com/backend/${group.profile_picture}`)
                                    : '/images/default.png';
                                return (
                                    <li key={`group-${group.id}`} onClick={() => handleMentionClick(group, 'group')}>
                                        <img src={photo} alt="" className="mention-avatar" onError={e => { e.target.src = '/images/default.png'; }} />
                                        <span>{group.name} <small style={{ opacity: 0.6 }}>· Group</small></span>
                                    </li>
                                );
                            })}
                            {mentionResults.companies.map((company) => {
                                const photo = company.profile_picture
                                    ? (company.profile_picture.startsWith('http') ? company.profile_picture : `https://buzzinguniverse.com/backend/${company.profile_picture}`)
                                    : '/images/default.png';
                                return (
                                    <li key={`company-${company.id}`} onClick={() => handleMentionClick(company, 'company')}>
                                        <img src={photo} alt="" className="mention-avatar" onError={e => { e.target.src = '/images/default.png'; }} />
                                        <span>{company.name} <small style={{ opacity: 0.6 }}>· Company</small></span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Attachment preview */}
                    {attachment && (
                        <div className="cb-attachment">
                            <img
                                src={attachment.type === 'gif' ? attachment.url : attachment.previewUrl}
                                alt="attachment"
                            />
                            <button type="button" className="cb-attachment-remove" onClick={removeAttachment}>×</button>
                        </div>
                    )}
                </form>

                {/* Emoji picker */}
                {showEmojiPicker && (
                    <div className="cb-emoji-popup" ref={emojiPickerRef}>
                        <EmojiPicker
                            onEmojiClick={insertEmoji}
                            width={300}
                            height={370}
                            theme="light"
                            searchDisabled={false}
                            skinTonesDisabled={false}
                            previewConfig={{ showPreview: false }}
                        />
                    </div>
                )}

                {/* GIF picker */}
                {showGifPicker && (
                    <div className="cb-gif-popup" ref={gifPickerRef}>
                        <input
                            className="cb-gif-search"
                            type="text"
                            placeholder="Search GIFs..."
                            value={gifSearch}
                            onChange={handleGifSearchChange}
                            autoFocus
                        />
                        <div className="cb-gif-grid">
                            {gifLoading && <div className="cb-gif-loading">Loading...</div>}
                            {!gifLoading && gifs.length === 0 && (
                                <div className="cb-gif-loading">No GIFs found</div>
                            )}
                            {!gifLoading && gifs.map((gif) => (
                                <img
                                    key={gif.id}
                                    src={gif.images.fixed_height_small.url}
                                    alt={gif.title}
                                    className="cb-gif-item"
                                    onClick={() => selectGif(gif)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommentBox;
